import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsSixVerticalIcon, TrashIcon, XIcon } from '@phosphor-icons/react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from '@/components/Image';
import { Button } from '@/components/ui/Button';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';
import { useGrabbingCursor } from '@/hooks/useGrabbingCursor';

interface ThumbnailItemRowProps {
    id: string;
    previewUrl: string;
    onRemove: () => void;
}

export function ThumbnailItemRow({
    id,
    previewUrl,
    onRemove,
}: ThumbnailItemRowProps) {
    const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.REPORTS]);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id,
        animateLayoutChanges: () => false,
        transition: {
            duration: 200,
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
        },
    });

    useGrabbingCursor(isDragging);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <>
            <div
                ref={setNodeRef}
                style={style}
                className={cn(
                    'group flex w-full min-w-0 flex-row items-start justify-start gap-3 rounded-md py-2',
                    'transition-colors hover:bg-grey-500',
                    isDragging && 'z-10 opacity-90',
                )}
            >
                <button
                    type="button"
                    className={cn(
                        'flex shrink-0 touch-none items-start justify-center rounded-md',
                        'cursor-grab text-grey-200 transition-colors group-hover:text-white-100',
                    )}
                    aria-label="Reorder"
                    {...attributes}
                    {...listeners}
                >
                    <DotsSixVerticalIcon className="h-5 w-5" weight="bold" />
                </button>
                <button
                    type="button"
                    className={cn(
                        'h-[76px] w-[136px] shrink-0 overflow-hidden rounded bg-grey-600',
                        'cursor-zoom-in border-0 p-0 outline-none ring-offset-2 ring-offset-transparent',
                        'focus-visible:ring-2 focus-visible:ring-white-100',
                    )}
                    onClick={() => setIsPreviewOpen(true)}
                >
                    <Image
                        src={previewUrl}
                        alt=""
                        auth
                        generatingPreview
                        className="pointer-events-none h-full w-full object-cover"
                    />
                </button>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="mt-0.5 self-start text-grey-200 hover:text-white-100"
                    onClick={onRemove}
                    aria-label={t('actions.delete', {
                        ns: I18nNamespace.COMMON,
                    })}
                    leadingIcon={<TrashIcon className="h-4 w-4 shrink-0" />}
                />
            </div>
            <DialogPrimitive.Root
                open={isPreviewOpen}
                onOpenChange={setIsPreviewOpen}
            >
                <DialogPrimitive.Portal>
                    <DialogPrimitive.Overlay
                        className={cn(
                            'fixed inset-0 z-[60] bg-black-100/80 backdrop-blur-md',
                            'data-[state=open]:animate-in data-[state=closed]:animate-out',
                            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                            'duration-100',
                        )}
                    />
                    <DialogPrimitive.Content
                        className={cn(
                            'fixed inset-0 z-[60] flex flex-col border-0 bg-transparent p-0 shadow-none outline-none',
                            'data-[state=open]:animate-in data-[state=closed]:animate-out',
                            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                            'duration-100',
                        )}
                        aria-describedby={undefined}
                    >
                        <DialogPrimitive.Title className="sr-only">
                            {t('eventEditor.thumbnailPreviewTitle', {
                                ns: I18nNamespace.REPORTS,
                            })}
                        </DialogPrimitive.Title>
                        <div
                            role="presentation"
                            className="relative flex h-full min-h-0 w-full flex-1 items-center justify-center p-4 pt-14 sm:p-6 sm:pt-16"
                            onClick={() => setIsPreviewOpen(false)}
                        >
                            <DialogPrimitive.Close asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="absolute right-3 top-3 z-10 text-grey-200 hover:text-white-100 sm:right-5 sm:top-5"
                                    leadingIcon={<XIcon className="h-5 w-5" />}
                                    aria-label={t('modal.close')}
                                    onClick={(event) => event.stopPropagation()}
                                />
                            </DialogPrimitive.Close>
                            <div
                                className="flex w-[min(74vw,40rem)] max-w-full shrink-0 justify-center"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <Image
                                    src={previewUrl}
                                    alt=""
                                    auth
                                    generatingPreview
                                    className="h-auto w-full max-h-[min(65dvh,68vw)] rounded-xl object-contain"
                                />
                            </div>
                        </div>
                    </DialogPrimitive.Content>
                </DialogPrimitive.Portal>
            </DialogPrimitive.Root>
        </>
    );
}
